/**
 * Scroll Utility Functions
 * 
 * Utility functions for handling scroll behavior in the application.
 * Provides cross-browser compatible scroll-to-top functionality with support
 * for various scrollable containers and scenarios.
 * 
 * Features:
 * - Multi-method scroll-to-top for maximum compatibility
 * - Support for window, document, and container scrolling
 * - Delayed scroll support for async DOM updates
 * - Works with Bootstrap containers and custom scrollable elements
 * 
 * Used by:
 * - Components that need to scroll to top on navigation
 * - Tab switching components
 * - Route change handlers
 * 
 * @module scrollUtils
 */

/**
 * Scrolls to the top of the page using multiple methods for maximum compatibility.
 * 
 * This function uses multiple approaches to ensure scrolling works across
 * different browsers, scenarios, and scrollable containers. It handles:
 * - Main window scrolling (modern browsers)
 * - Document element scrolling (HTML element)
 * - Body element scrolling (legacy browsers)
 * - CSS overflow containers (elements with overflow styles)
 * - Bootstrap containers (common CSS classes)
 * - All DOM elements with scrollTop property
 * 
 * Why multiple methods?
 * - Different browsers handle scrolling differently
 * - Some containers have their own scroll context
 * - Ensures scrolling works even if one method fails
 * 
 * @function
 * 
 * @example
 * ```typescript
 * // Scroll to top immediately
 * scrollToTop();
 * 
 * // Scroll to top after route change
 * useEffect(() => {
 *   scrollToTop();
 * }, [location]);
 * ```
 */
export const scrollToTop = () => {
  /**
   * Method 1: Standard window scroll.
   * Works in modern browsers and is the most reliable method.
   */
  window.scrollTo(0, 0);
  
  /**
   * Method 2: Direct DOM manipulation.
   * Works in older browsers and handles edge cases.
   * - documentElement: The <html> element
   * - body: The <body> element (for legacy support)
   */
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  
  /**
   * Method 3: Check for any scrollable containers with overflow styles.
   * Elements with CSS overflow property can have their own scroll context.
   */
  const scrollableElements = document.querySelectorAll('[style*="overflow"]');
  scrollableElements.forEach(element => {
    if (element.scrollTop > 0) {
      element.scrollTop = 0;
    }
  });
  
  /**
   * Method 4: Check for Bootstrap containers.
   * Common CSS classes used in Bootstrap layouts that might have scrolling.
   */
  const containers = document.querySelectorAll('.container, .container-fluid, .row, .col, .main-content');
  containers.forEach(container => {
    if (container.scrollTop > 0) {
      container.scrollTop = 0;
    }
  });
  
  /**
   * Method 5: Check for any element with scrollTop property.
   * Fallback method to catch any scrollable elements we might have missed.
   * Note: This is comprehensive but may have performance impact on large DOMs.
   */
  const allElements = document.querySelectorAll('*');
  allElements.forEach(element => {
    if (element.scrollTop > 0) {
      element.scrollTop = 0;
    }
  });
};

/**
 * Scrolls to top with a delay to ensure DOM is updated.
 * 
 * Useful when scrolling needs to happen after:
 * - DOM updates (route changes, tab switches)
 * - State updates that affect layout
 * - Async operations that modify the page
 * - Component mount/unmount cycles
 * 
 * The delay allows the browser to finish rendering before scrolling,
 * ensuring the scroll position is correctly calculated.
 * 
 * @param delay - Delay in milliseconds before scrolling (default: 0)
 * @function
 * 
 * @example
 * ```typescript
 * // Scroll after 100ms delay
 * scrollToTopDelayed(100);
 * 
 * // Scroll after route change with delay
 * useEffect(() => {
 *   scrollToTopDelayed(100);
 * }, [location]);
 * ```
 */
export const scrollToTopDelayed = (delay: number = 0) => {
  setTimeout(() => {
    scrollToTop();
  }, delay);
};
